import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../api/client';

/**
 * Feature flags from GET /api/config/feature-flags (non-sensitive, server-driven).
 * Defaults are FAIL-CLOSED: when the endpoint is unreachable or not yet loaded,
 * optional features (AI UI) stay hidden and auth buttons stay hidden.
 */
interface FeatureFlagsContextType {
  enableAiBuddy: boolean;
  enableAnalytics: boolean;
  bridgeLoginEnabled: boolean;
  localLoginEnabled: boolean;
  loading: boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  enableAiBuddy: false,
  enableAnalytics: false,
  bridgeLoginEnabled: false,
  localLoginEnabled: false,
  loading: true,
});

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const [enableAiBuddy, setEnableAiBuddy] = useState(false);
  const [enableAnalytics, setEnableAnalytics] = useState(false);
  const [bridgeLoginEnabled, setBridgeLoginEnabled] = useState(false);
  const [localLoginEnabled, setLocalLoginEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    api.get<{ enableAiBuddy: boolean; enableAnalytics?: boolean; bridgeLoginEnabled?: boolean; localLoginEnabled?: boolean }>('/config/feature-flags')
      .then((flags) => {
        if (cancelled) return;
        setEnableAiBuddy(!!flags?.enableAiBuddy);
        setEnableAnalytics(!!flags?.enableAnalytics);
        setBridgeLoginEnabled(!!flags?.bridgeLoginEnabled);
        setLocalLoginEnabled(!!flags?.localLoginEnabled);
      })
      .catch(() => {
        // Unreachable endpoint — keep the fail-closed defaults.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return (
    <FeatureFlagsContext.Provider value={{ enableAiBuddy, enableAnalytics, bridgeLoginEnabled, localLoginEnabled, loading }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}
